
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, MoreHorizontal, Plus, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Plan } from '@/api/plans';
import { AddPlanDialog } from './add-plan-dialog';
import { EditPlanDialog } from './edit-plan-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { useGym } from '@/contexts/gym-context';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useConfirmationStore } from '@/lib/stores/confirmation-store';

export function PlanTable() {
  const { t } = useTranslation();
  const { selectedGym } = useGym();
  const { toast } = useToast();
  const confirmationStore = useConfirmationStore();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Fetch plans manually
  const fetchPlans = useCallback(
    async (gymId: string) => {
      if (!gymId) return;

      setIsLoading(true);
      setError(null);

      try {
        const url = `/plans`;
        console.log(`Manually fetching plans from: ${url}`);

        const response = await apiClient.get(url);
        setPlans(response.data || []);
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError('Failed to load plans. Please try again.');
        setPlans([]);

        toast({
          title: 'Error',
          description: 'Failed to load plans. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [toast]
  );

  // Fetch plans when the component mounts or when the gym changes
  useEffect(() => {
    if (selectedGym) {
      console.log(`Selected gym changed to ${selectedGym}, fetching plans`);
      fetchPlans(selectedGym);
    }
  }, [selectedGym, fetchPlans]);

  // Handle plan deletion
  const handleDeletePlan = useCallback(
    (plan: Plan) => {
      confirmationStore.open({
        title: t('plans.deletePlan'),
        message: t('plans.deleteConfirmNamed', { name: plan.name }),
        variant: 'destructive',
        confirmLabel: t('common.delete'),
        onConfirm: async () => {
          try {
            console.log(`Deleting plan ${plan._id}...`);
            await apiClient.delete(`/plans/${plan._id}`);

            toast({
              title: t('common.success'),
              description: t('plans.planDeleted'),
            });

            if (selectedGym) {
              fetchPlans(selectedGym);
            }
          } catch (error) {
            console.error(`Error deleting plan ${plan._id}:`, error);

            toast({
              title: t('common.error'),
              description: t('plans.deleteError'),
              variant: 'destructive',
            });
          }
        },
      });
    },
    [fetchPlans, selectedGym, t, toast, confirmationStore]
  );

  const columns: ColumnDef<Plan>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.name')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
    },
    {
      accessorKey: 'price',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.price')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div>{formatCurrency(row.original.price, row.original.currency)}</div>
      ),
    },
    {
      accessorKey: 'duration',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.duration')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const plan = row.original;
        if(plan.durationType === 'fixed'){
          return <div>{t('plans.fixed')}</div>;
        }
        const durationText =
          plan.duration === 1
            ? t(`plans.${plan.durationType.slice(0, -1)}`) // Remove 's' for singular
            : t(`plans.${plan.durationType}`);
        return <div>{`${plan.duration} ${durationText}`}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {t('plans.status')}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <div
            className={`font-medium ${
              status === 'active' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {status === 'active' ? t('plans.active') : t('plans.inactive')}
          </div>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const plan = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">{t('common.openMenu')}</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedPlan(plan);
                  setEditDialogOpen(true);
                }}
              >
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeletePlan(plan)}
                className="text-red-600"
              >
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: plans,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  if (!selectedGym) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-10">
            <p className="text-muted-foreground">{t('plans.selectGymFirst')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('plans.title')}</CardTitle>
        <CardDescription>{t('plans.description')}</CardDescription>
        <div className="flex items-center py-4 justify-between">
          <Input
            placeholder={t('plans.filterByName')}
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> {t('plans.addPlan')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex justify-center items-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-destructive"
                  >
                    {error}
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t('plans.noPlans')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {t('common.pageInfo', {
              currentPage: table.getState().pagination.pageIndex + 1,
              totalPages: table.getPageCount(),
              totalItems: plans.length,
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {t('common.next')}
          </Button>
        </div>
      </CardContent>

      {/* Dialogs */}
      <AddPlanDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={() => {
          if (selectedGym) fetchPlans(selectedGym);
        }}
      />

      {editDialogOpen && selectedPlan && (
        <EditPlanDialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) {
              setSelectedPlan(null);
              if (selectedGym) fetchPlans(selectedGym);
            }
          }}
          plan={selectedPlan}
        />
      )}
    </Card>
  );
}
